/**
 * Supply composer (Phase 92, UI-SPEC Screen 1 sections 1.1 and 1.2).
 *
 * A client builds a product on its 3PL's blank garment, inside Tier 1 of the
 * expanded product row:
 *   - `SupplySourceSwitch`: "My own blanks" | "{owner}'s blanks" (one 3PL) or
 *     "A 3PL's blanks" plus an owner Select (2 to 6) or Combobox (7 or more,
 *     DESIGN.md §13.9). Admins only; a Coordinator reads the source as text.
 *     A client that prints only on the blanks of its 3PL (blankSource supply)
 *     never sees "My own blanks": only the 3PL picker, if there are two or more.
 *   - `SupplyOwnersError`: the 3PL list failed; Tier 1 stays as today plus
 *     one muted line and a retry (UI 29).
 *   - `SupplyComposer`: three gated steps (DESIGN.md §4). Step 1 chooses the
 *     3PL's garment (`SharedSkuPicker mode="garment"`); step 2 matches sizes
 *     and colours and picks light or dark per colour (not for a custom
 *     product, where it is hidden but still saved as today)
 *     (`BlankValueMatchTable`, fed by `useSupplyComposer`'s last preview);
 *     step 3 unlocks when that preview reports `complete` and shows the print
 *     SKUs (`SupplyPreviewStep`).
 *   - The footer (92-27): "Discard draft" and "Use this blank", which saves
 *     the client's choice once (`productSync.saveSupplyElection`). It never
 *     writes to Shopify; the next Sync selected run builds the print SKUs.
 *     On success the footer becomes the saved note; a refusal shows inline
 *     and as a sticky toast, and the draft stays.
 *
 *   - BLU-1522: under step 1, a quiet link "Use different base garments for
 *     some colours or sizes" opens a "Per colour / Per size" switch. Step 2's
 *     table then ticks rows of that option; "Assign base garment" puts the
 *     ticked rows on another of the 3PL's garments, the preview auto-matches
 *     them there, and only misses show a picker (value names, no codes).
 *     Without the link the composer is exactly the one-garment flow.
 *
 *   - BLU-1522 S2b: "Change blank" on a product with synced variants opens
 *     with those variants locked: their rows show "Already synced, can't
 *     change" with no picker, no tick and no light or dark switch, the
 *     default garment cannot change, and the split cannot change while a
 *     synced variant sits on another base garment. New colours or sizes, and
 *     the variants not synced yet, stay editable. The save still decides.
 *
 * The draft is local state until saved. "Discard draft", or switching back to
 * "My own blanks" (which unmounts the composer), drops it with no confirm.
 */
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, CheckCircle2, ChevronsUpDown, Link2, Loader2, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { NoteLine } from '@/components/shared/NoteLine';
import { SectionError } from '@/components/shared/SectionError';
import { cn } from '@/lib/utils';
import type { RouterOutputs } from '@/lib/trpc';

import { SharedSkuPicker, type SharedGarmentOption, type UseGarmentOptions } from '../shared-stock/SharedSkuPicker';
import {
  supplyDesignSetsFromStaging,
  supplyValueKey,
  useSupplyComposer,
  SUPPLY_MAX_GARMENT_GROUPS,
  type SupplyComposePreviewOk,
  type SupplyDraft,
  type SupplyElection,
  type SupplyMatchGroup,
  type SupplyValueChoice,
} from '../../hooks/useSupplyComposer';
import type { SupplyPillState } from '../StatusPill';
import type { ProductStaging } from '../../hooks/useStagedDesigns';
import type { PreviewProduct } from '../../lib/types';
import { productValues } from '../../lib/garment-groups';
import { BlankValueMatchTable, type BlankValueMatchRow } from './BlankValueMatchTable';
import { SupplyPreviewStep } from './SupplyPreviewStep';

/** One 3PL the client is connected to (`inventory.listSharedStockOwners`). */
export type SupplyOwner = RouterOutputs['inventory']['listSharedStockOwners']['owners'][number];

export type SupplySource = 'own' | 'supply';

/** DESIGN.md §13.9: a Select up to 6 owners, a searchable Combobox from 7. */
const OWNER_SELECT_MAX = 6;

// ---------------------------------------------------------------------------
// Source switch (UI-SPEC 1.1)
// ---------------------------------------------------------------------------

export interface SupplySourceSwitchProps {
  productId: string;
  /** At least one; the caller renders nothing for zero (UI 1). */
  owners: SupplyOwner[];
  source: SupplySource;
  /** The chosen 3PL; with one owner it is that owner. */
  ownerId: string | null;
  onSourceChange: (source: SupplySource) => void;
  onOwnerChange: (ownerId: string) => void;
  /** Only admins change the source; a Coordinator reads it as text. */
  admin: boolean;
  /**
   * BLU-1522 S8: why the 3PL's blanks can't be chosen (the product uses the
   * org's own base garments per colour or size). Set: that segment is
   * disabled and these words show under the switch.
   */
  supplyDisabledReason?: string;
  /**
   * BLU-1522 S6a: the client prints only on its 3PL's blanks, so there is no
   * "My own blanks" to switch to. Only the 3PL picker remains (two or more
   * 3PLs); with one 3PL the switch renders nothing, the title names it.
   */
  supplyOnly?: boolean;
}

export function SupplySourceSwitch({
  productId,
  owners,
  source,
  ownerId,
  onSourceChange,
  onOwnerChange,
  admin,
  supplyDisabledReason,
  supplyOnly = false,
}: SupplySourceSwitchProps) {
  const { t } = useTranslation('productSync');
  const single = owners.length === 1 ? owners[0]! : null;
  const chosen = owners.find((o) => o.id === ownerId) ?? single;

  if (!admin) {
    const current =
      source === 'supply' && chosen
        ? t('supply.source.owner', { owner: chosen.name })
        : supplyOnly
          ? t('supply.source.anyOwner')
          : t('supply.source.own');
    return <p className="text-xs text-muted-foreground">{t('supply.source.current', { source: current })}</p>;
  }

  if (supplyOnly && owners.length < 2) return null;

  const ownerLabel = single ? t('supply.source.owner', { owner: single.name }) : t('supply.source.anyOwner');
  const ownerControlId = `supply-owner-${productId}`;

  const control = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      {!supplyOnly && (
        <SegmentedControl<SupplySource>
          size="sm"
          ariaLabel={t('supply.source.label')}
          value={source}
          onChange={onSourceChange}
          className="max-w-full"
          options={[
            { value: 'own', label: t('supply.source.own') },
            { value: 'supply', label: ownerLabel, title: ownerLabel, ...(supplyDisabledReason ? { disabled: true } : {}) },
          ]}
        />
      )}
      {(supplyOnly || source === 'supply') && owners.length > 1 && (
        <div className="flex min-w-0 items-center gap-2">
          <Label htmlFor={ownerControlId} className="shrink-0 text-sm font-medium">
            {t('supply.owner.label')}
            <span aria-hidden="true" className="ml-0.5 text-destructive">
              *
            </span>
          </Label>
          {owners.length <= OWNER_SELECT_MAX ? (
            <Select value={ownerId ?? ''} onValueChange={onOwnerChange}>
              <SelectTrigger
                id={ownerControlId}
                aria-required
                aria-invalid={!chosen}
                title={chosen?.name}
                className={cn(
                  'h-8 w-56 max-w-full text-xs transition-colors duration-200 motion-reduce:transition-none',
                  !chosen && 'border-amber-300 ring-1 ring-amber-100/50',
                )}
              >
                <SelectValue placeholder={t('supply.owner.placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {owners.map((owner) => (
                  <SelectItem key={owner.id} value={owner.id} title={owner.name}>
                    <span className="block max-w-[14rem] truncate">{owner.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <OwnerCombobox id={ownerControlId} owners={owners} chosen={chosen} onOwnerChange={onOwnerChange} />
          )}
        </div>
      )}
    </div>
  );
  if (!supplyDisabledReason) return control;
  return (
    <div className="flex flex-col gap-1">
      {control}
      <p className="max-w-prose text-xs leading-normal text-muted-foreground" data-testid="supply-source-disabled">
        {supplyDisabledReason}
      </p>
    </div>
  );
}

function OwnerCombobox({
  id,
  owners,
  chosen,
  onOwnerChange,
}: {
  id: string;
  owners: SupplyOwner[];
  chosen: SupplyOwner | null;
  onOwnerChange: (ownerId: string) => void;
}) {
  const { t } = useTranslation('productSync');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-required
          aria-invalid={!chosen}
          title={chosen?.name}
          className={cn(
            'h-8 w-56 max-w-full justify-between gap-2 px-3 text-xs font-normal transition-colors duration-200 motion-reduce:transition-none',
            !chosen && 'border-amber-300 ring-1 ring-amber-100/50',
          )}
        >
          {chosen ? (
            <span className="truncate">{chosen.name}</span>
          ) : (
            <span className="truncate text-muted-foreground">{t('supply.owner.placeholder')}</span>
          )}
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 max-w-[calc(100vw-2rem)] p-0">
        <Command>
          <CommandInput value={search} onValueChange={setSearch} placeholder={t('supply.owner.search')} />
          <CommandList>
            <CommandEmpty className="px-3 py-6 text-center text-xs text-muted-foreground">
              {t('supply.owner.noMatch', { query: search.trim() })}
            </CommandEmpty>
            <CommandGroup>
              {owners.map((owner) => (
                <CommandItem
                  key={owner.id}
                  value={`${owner.name} ${owner.id}`}
                  title={owner.name}
                  onSelect={() => {
                    onOwnerChange(owner.id);
                    setOpen(false);
                  }}
                  className="gap-2"
                >
                  <Check
                    className={cn(
                      'h-3.5 w-3.5 shrink-0 text-threadheads-purple',
                      chosen?.id === owner.id ? 'opacity-100' : 'opacity-0',
                    )}
                    aria-hidden="true"
                  />
                  <span className="truncate text-xs">{owner.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** UI 29: the 3PL list failed. Tier 1 stays as today; this line sits above it. */
export function SupplyOwnersError({ onRetry, retrying }: { onRetry: () => void; retrying: boolean }) {
  const { t } = useTranslation('productSync');
  return (
    <div className="flex flex-wrap items-center gap-2">
      <p className="text-xs text-muted-foreground">{t('supply.ownersError')}</p>
      <Button
        variant="ghost"
        size="sm"
        className="h-8 gap-1.5 px-3 text-xs motion-reduce:transition-none"
        onClick={onRetry}
        disabled={retrying}
        aria-busy={retrying}
      >
        <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
        {t('supply.ownersRetry')}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gated steps (DESIGN.md §4, §16)
// ---------------------------------------------------------------------------

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** True for 1.2 s after `unlocked` goes false to true (DESIGN.md §4.2); never with reduced motion. */
function useJustUnlocked(unlocked: boolean): boolean {
  const [just, setJust] = useState(false);
  const was = useRef(unlocked);
  useEffect(() => {
    const before = was.current;
    was.current = unlocked;
    if (!unlocked) {
      setJust(false);
      return;
    }
    if (before || prefersReducedMotion()) return;
    setJust(true);
    const timer = window.setTimeout(() => setJust(false), 1200);
    return () => window.clearTimeout(timer);
  }, [unlocked]);
  return just;
}

interface ComposerStepProps {
  /** Heading text (the step label), or null when a control's label is the step label. */
  label: string | null;
  locked: boolean;
  /** Amber line naming what unlocks the step (shown only while locked). */
  lockedHelper?: string;
  /** Id of the amber line, so the footer's primary can point at it. */
  helperId?: string;
  /** Focus target for an unlock or an Enter walk. */
  stepRef?: React.Ref<HTMLElement>;
  children?: ReactNode;
  testId: string;
}

/**
 * One step. Locked: the body is dimmed, takes no pointer and is `inert`, so a
 * keyboard or screen reader skips it (§16); the amber helper sits OUTSIDE the
 * inert part so it is still read.
 */
function ComposerStep({ label, locked, lockedHelper, helperId, stepRef, children, testId }: ComposerStepProps) {
  const headingId = useId();
  const glow = useJustUnlocked(!locked);
  return (
    <section
      ref={stepRef}
      tabIndex={-1}
      aria-labelledby={label ? headingId : undefined}
      data-testid={testId}
      data-locked={locked ? 'true' : 'false'}
      className={cn(
        'flex flex-col gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        glow && 'motion-safe:animate-unlock-glow',
      )}
    >
      <div
        className={cn(
          'flex flex-col gap-2 transition-opacity duration-200 motion-reduce:transition-none',
          locked && 'pointer-events-none select-none opacity-60',
        )}
        {...(locked ? ({ inert: '' } as { inert: '' }) : {})}
      >
        {label && (
          <h4 id={headingId} className="text-sm font-medium">
            {label}
          </h4>
        )}
        {children}
      </div>
      {locked && lockedHelper && (
        <p id={helperId} className="text-xs leading-normal text-amber-700">
          {lockedHelper}
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// The composer (UI-SPEC 1.2)
// ---------------------------------------------------------------------------

export interface SupplyComposerProps {
  product: PreviewProduct;
  owner: SupplyOwner;
  /** The composer is admin only; the row never mounts it for anyone else. */
  admin: boolean;
  /** Injected garment option source (D-21); defaults to the 3PL's shared garments. */
  useGarmentOptions?: UseGarmentOptions;
  /** The product's staged designs: which print sets carry a back, and whether the single set is staged. */
  staged?: ProductStaging;
  /** "Discard draft" also returns the source switch to "My own blanks". */
  onDiscard?: () => void;
  /**
   * "Change blank" (92-27b): the saved choice the draft opens with, once, on
   * mount. A choice with synced variants opens too, with their rows locked
   * (BLU-1522 S2b).
   */
  initialElection?: SupplyElection | null;
  /** The save succeeded (92-27b: "Change blank" closes the composer and shows the saved summary). */
  onSaved?: () => void;
  /** The header pill's supply state for this draft (UI-SPEC 1.5); null when none applies or on unmount. */
  onPillStateChange?: (state: SupplyPillState | null) => void;
  /** The product is custom (the row's decision): step 2 shows no light or dark choice. What is saved does not change. */
  customProduct?: boolean;
}

export function SupplyComposer({
  product,
  owner,
  admin,
  useGarmentOptions,
  staged,
  onDiscard,
  initialElection,
  onSaved,
  onPillStateChange,
  customProduct = false,
}: SupplyComposerProps) {
  const { t } = useTranslation('productSync');

  if (!admin) return null;

  // UI 2: the 3PL has not turned its blanks on for this store. No picker.
  if (owner.blanksAllowed !== true) {
    return (
      <p className="flex items-start gap-2 text-xs leading-normal text-amber-800">
        <Link2 className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span>{t('supply.consentOff', { owner: owner.name })}</span>
      </p>
    );
  }

  return (
    <SupplyComposerSteps
      product={product}
      owner={owner}
      useGarmentOptions={useGarmentOptions}
      staged={staged}
      onDiscard={onDiscard}
      initialElection={initialElection ?? null}
      onSaved={onSaved}
      onPillStateChange={onPillStateChange}
      customProduct={customProduct}
    />
  );
}

/**
 * The client's own choices shown at once over the last preview, so a pick
 * never flickers back while the next preview runs (the server re-derives the
 * same `how` from what was sent).
 */
function withDraft(
  rows: ReadonlyArray<BlankValueMatchRow>,
  choices: Record<string, SupplyValueChoice>,
): BlankValueMatchRow[] {
  return rows.map((row) => {
    const choice = choices[supplyValueKey(row.storeValue)];
    if (!choice) return row;
    return choice.blankValue === null
      ? { ...row, blankValue: null, how: 'left_out' as const }
      : { ...row, blankValue: choice.blankValue, how: 'chosen' as const };
  });
}

type RowGarment = NonNullable<BlankValueMatchRow['garment']>;

/** Whether a row carries a synced variant (`useSupplyComposer`'s `isRowLocked`). */
type RowLocked = (group: SupplyMatchGroup, storeValue: string | null, garmentKey?: string) => boolean;

function withLock(row: BlankValueMatchRow, locked: boolean): BlankValueMatchRow {
  return locked ? { ...row, locked: true } : row;
}

/**
 * Step 2's rows with the product split over garments. The split
 * option's rows are the product's own values (so a tick never waits on the
 * preview), each on its garment, with that garment's match from the last
 * preview (or `pending` until the preview has it). The other option's rows
 * come per garment, default first. The client's own picks show over the
 * last preview, as in the one-garment table.
 */
function groupedMatchRows(
  ok: SupplyComposePreviewOk,
  draft: SupplyDraft,
  garment: SharedGarmentOption,
  product: Pick<PreviewProduct, 'variants'>,
  split: SupplyMatchGroup,
  isRowLocked: RowLocked,
): { colours: BlankValueMatchRow[]; sizes: BlankValueMatchRow[] } {
  const listOf = (dimension: SupplyMatchGroup): 'colours' | 'sizes' => (dimension === 'colour' ? 'colours' : 'sizes');
  const choicesOf = (dimension: SupplyMatchGroup): 'colourMatches' | 'sizeMatches' =>
    dimension === 'colour' ? 'colourMatches' : 'sizeMatches';
  const asGarment = (g: { garmentKey: string; title: string; imageUrl: string | null }): RowGarment => ({
    key: g.garmentKey.toUpperCase(),
    title: g.title,
    imageUrl: g.imageUrl,
  });
  type Part = {
    garment: RowGarment;
    choices: Pick<SupplyDraft, 'colourMatches' | 'sizeMatches'>;
    storeValues: Set<string> | null;
    preview: Pick<SupplyComposePreviewOk, 'matches' | 'blankValues'> | null;
  };
  const main: Part = { garment: asGarment(garment), choices: draft, storeValues: null, preview: ok };
  const grouped: Part[] = draft.groups.map((group) => {
    const key = group.garment.garmentKey.toUpperCase();
    return {
      garment: asGarment(group.garment),
      choices: group,
      storeValues: new Set(group.storeValues.map(supplyValueKey)),
      preview: (ok.groups ?? []).find((g) => g.garment.garmentKey.toUpperCase() === key) ?? null,
    };
  });
  const other: SupplyMatchGroup = split === 'colour' ? 'size' : 'colour';

  const splitRows: BlankValueMatchRow[] = productValues(product, split).map(({ value, count }) => {
    const key = supplyValueKey(value);
    const part = grouped.find((p) => p.storeValues?.has(key)) ?? main;
    const locked = isRowLocked(split, value);
    // A garment with no values of this option (an unsized tote, per size) has nothing to match.
    if (part.preview && part.preview.blankValues[listOf(split)].length === 0) {
      return withLock(
        { storeValue: value, variantCount: count, blankValue: null, how: 'auto', garment: part.garment, noValues: true },
        locked,
      );
    }
    const found = part.preview?.matches[listOf(split)].find((row) => supplyValueKey(row.storeValue) === key);
    if (!part.preview || !found) {
      return withLock(
        { storeValue: value, variantCount: count, blankValue: null, how: 'unmatched', garment: part.garment, pending: true },
        locked,
      );
    }
    const [row] = withDraft([found], part.choices[choicesOf(split)]);
    return withLock({ ...row!, garment: part.garment, options: part.preview.blankValues[listOf(split)] }, locked);
  });
  const otherRows: BlankValueMatchRow[] = [main, ...grouped].flatMap((part) => {
    const preview = part.preview;
    if (!preview) return [];
    return withDraft(preview.matches[listOf(other)], part.choices[choicesOf(other)]).map((row) =>
      withLock(
        { ...row, garment: part.garment, options: preview.blankValues[listOf(other)] },
        isRowLocked(other, row.storeValue, part.garment.key),
      ),
    );
  });
  return split === 'colour' ? { colours: splitRows, sizes: otherRows } : { colours: otherRows, sizes: splitRows };
}

function MatchSkeleton() {
  // UI 8 (match part): four `h-10` rows, never a page spinner.
  return (
    <div className="space-y-2" aria-hidden="true" data-testid="supply-match-skeleton">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
    </div>
  );
}

function SupplyComposerSteps({
  product,
  owner,
  useGarmentOptions,
  staged,
  onDiscard,
  initialElection,
  onSaved,
  onPillStateChange,
  customProduct,
}: {
  product: PreviewProduct;
  owner: SupplyOwner;
  useGarmentOptions?: UseGarmentOptions;
  staged?: ProductStaging;
  onDiscard?: () => void;
  initialElection: SupplyElection | null;
  onSaved?: () => void;
  onPillStateChange?: (state: SupplyPillState | null) => void;
  customProduct: boolean;
}) {
  const { t } = useTranslation('productSync');
  const designSets = useMemo(() => supplyDesignSetsFromStaging(staged), [staged]);
  const composer = useSupplyComposer(product, owner, designSets);
  const { draft, preview, previewError, previewLoading, openWith, isRowLocked, garmentLocked, splitLocked, lock } = composer;

  // "Change blank": seed the draft from the saved choice once (BLU-1522 S2b:
  // a choice with synced variants opens with those rows locked).
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current || !initialElection) return;
    opened.current = true;
    openWith(initialElection);
  }, [initialElection, openWith]);

  // The parent's callbacks, read through refs so a new function each render
  // never re-runs the effects below.
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const onPillRef = useRef(onPillStateChange);
  onPillRef.current = onPillStateChange;
  const isSaved = composer.saved !== null;
  useEffect(() => {
    if (isSaved) onSavedRef.current?.();
  }, [isSaved]);
  const step2Ref = useRef<HTMLElement>(null);
  const step3Ref = useRef<HTMLElement>(null);
  const step2HelperId = useId();
  const step3HelperId = useId();
  const [announcement, setAnnouncement] = useState('');
  // Set when a garment is chosen; the first preview for it moves focus.
  const focusStep2 = useRef(false);

  const ok = preview?.ok ? preview : null;
  const complete = ok?.complete === true;
  const garmentChosen = draft.garment !== null;
  const split = draft.split;

  // BLU-1522: the rows ticked for "Assign base garment" (store value keys),
  // cleared when the split or the default garment changes.
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [tooMany, setTooMany] = useState(false);
  const defaultGarmentKey = draft.garment?.garmentKey;
  useEffect(() => {
    setSelected(new Set());
    setTooMany(false);
  }, [split, defaultGarmentKey]);
  // Which options can split the product: those the server matched on more
  // than one store value (so never one it ignores, such as a single-size
  // type). Kept from the last answered preview, so a refusal still shows the
  // way back to one base garment.
  const [splittable, setSplittable] = useState({ colour: false, size: false });
  useEffect(() => {
    if (!ok) return;
    const splits = (list: 'colours' | 'sizes') =>
      new Set(
        [ok.matches[list], ...(ok.groups ?? []).map((g) => g.matches[list])]
          .flat()
          .filter((row) => row.storeValue !== null)
          .map((row) => supplyValueKey(row.storeValue)),
      ).size > 1;
    setSplittable({ colour: splits('colours'), size: splits('sizes') });
  }, [ok]);
  const matchRows = useMemo(() => {
    if (!ok) return null;
    const rows =
      split && draft.garment
        ? groupedMatchRows(ok, draft, draft.garment, product, split, isRowLocked)
        : {
            colours: withDraft(ok.matches.colours, draft.colourMatches).map((row) =>
              withLock(row, isRowLocked('colour', row.storeValue)),
            ),
            sizes: withDraft(ok.matches.sizes, draft.sizeMatches).map((row) =>
              withLock(row, isRowLocked('size', row.storeValue)),
            ),
          };
    // BLU-1522 S2b: light or dark is one choice per store colour, so a colour
    // with a synced variant on ANY base garment keeps it on every row (split
    // per size lists the colour once per garment; only some of those rows
    // carry the synced variant).
    const colours = lock
      ? rows.colours.map((row) => (lock.colours.has(supplyValueKey(row.storeValue)) ? { ...row, designLocked: true } : row))
      : rows.colours;
    return { colours, sizes: rows.sizes };
  }, [ok, split, draft, product, isRowLocked, lock]);

  // The header pill for this draft (UI-SPEC 1.5): no blank yet; values still
  // to match (the client's own picks shown over the last preview); or a print
  // SKU already in use. Reported only when it changes, and cleared on unmount.
  let pill: SupplyPillState | null = null;
  if (!garmentChosen) {
    pill = { kind: 'choose_blank' };
  } else if (ok && !complete) {
    const unmatched = [...(matchRows?.colours ?? []), ...(matchRows?.sizes ?? [])].filter(
      (row) => row.how === 'unmatched' && !row.pending,
    ).length;
    if (unmatched > 0) pill = { kind: 'match_values', count: unmatched };
  } else if (ok && complete && ok.rows.some((row) => row.status === 'in_use')) {
    pill = { kind: 'print_sku_in_use' };
  }
  const pillKey = pill ? JSON.stringify(pill) : '';
  useEffect(() => {
    onPillRef.current?.(pillKey ? (JSON.parse(pillKey) as SupplyPillState) : null);
  }, [pillKey]);
  useEffect(() => () => onPillRef.current?.(null), []);

  // Announce step 3 once it unlocks (false to true), politely.
  const wasComplete = useRef(complete);
  useEffect(() => {
    if (complete && !wasComplete.current) setAnnouncement(t('supply.step3'));
    wasComplete.current = complete;
  }, [complete, t]);

  // Choosing a garment: focus the first unmatched row's control (else the
  // first control) once its match rows exist (UI-SPEC focal point).
  useEffect(() => {
    if (!focusStep2.current || !ok) return;
    focusStep2.current = false;
    const root = step2Ref.current;
    const target =
      root?.querySelector<HTMLElement>('[data-match-control="unmatched"]') ??
      root?.querySelector<HTMLElement>('[data-match-control]');
    target?.focus();
  }, [ok]);

  let step2Body: ReactNode = null;
  if (garmentChosen) {
    if (preview && !preview.ok) {
      const refusal =
        preview.refusal === 'consent_off'
          ? t('supply.consentOff', { owner: owner.name })
          : preview.refusal === 'blank_not_offered'
            ? t('supply.refusal.blankNotOffered', { owner: owner.name })
            : preview.refusal === 'split_unavailable'
              ? split === 'size'
                ? t('supply.refusal.splitUnavailableSize')
                : t('supply.refusal.splitUnavailableColour')
              : t('sharedStock.reason.connection_inactive');
      step2Body = <p className="text-xs leading-normal text-amber-800">{refusal}</p>;
    } else if (ok && matchRows) {
      const assign = (garment: SharedGarmentOption) => {
        if (!split) return;
        const values = productValues(product, split)
          .map((entry) => entry.value)
          .filter((value) => selected.has(supplyValueKey(value)));
        const outcome = composer.assignGarment(values, garment);
        setTooMany(outcome === 'too_many');
        if (outcome === 'assigned') {
          setSelected(new Set());
          setAnnouncement(t('supply.groups.assigned', { garment: garment.title, count: values.length }));
        }
      };
      step2Body = (
        <div className="flex flex-col gap-3">
          {previewError && !complete && (
            <SectionError
              variant="inline"
              body={t('supply.preview.error')}
              onRetry={composer.retryPreview}
              retryLabel={t('supply.preview.retry')}
              retrying={previewLoading}
            />
          )}
          {split && (
            <div className="flex flex-col gap-1.5" data-testid="supply-assign-bar">
              <p className="text-xs leading-normal text-muted-foreground">
                {split === 'colour' ? t('supply.groups.hintColour') : t('supply.groups.hintSize')}
              </p>
              <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                <div className="w-full max-w-sm">
                  <SharedSkuPicker
                    mode="garment"
                    id={`supply-assign-${product.id}`}
                    label={t('supply.groups.assign')}
                    placeholder={t('supply.groups.assignPlaceholder')}
                    required={false}
                    disabled={selected.size === 0}
                    ownerOrganizationId={owner.id}
                    ownerName={owner.name}
                    value={null}
                    onGarmentChange={assign}
                    useGarmentOptions={useGarmentOptions}
                  />
                </div>
                <p className="pb-2 text-xs tabular-nums text-muted-foreground" aria-live="polite">
                  {t('supply.groups.selected', { count: selected.size })}
                </p>
              </div>
              {tooMany && (
                <p className="text-xs leading-normal text-amber-700" role="alert">
                  {t('supply.groups.tooMany', { max: SUPPLY_MAX_GARMENT_GROUPS + 1 })}
                </p>
              )}
            </div>
          )}
          <BlankValueMatchTable
            idPrefix={`supply-match-${product.id}`}
            matches={matchRows}
            blankValues={ok.blankValues}
            designFor={composer.designFor}
            ownerName={owner.name}
            onMatch={composer.setMatch}
            onDesignChoice={composer.setDesignChoice}
            showDesignChoice={!customProduct}
            onWalkPastEnd={() => {
              if (complete) step3Ref.current?.focus();
            }}
            {...(split ? { grouped: true, selection: { group: split, selected, onChange: setSelected } } : {})}
          />
        </div>
      );
    } else if (previewError) {
      step2Body = (
        <SectionError
          variant="compact"
          body={t('supply.preview.error')}
          onRetry={composer.retryPreview}
          retryLabel={t('supply.preview.retry')}
          retrying={previewLoading}
        />
      );
    } else {
      step2Body = <MatchSkeleton />;
    }
  }

  // The save gate (UI-SPEC 1.2 footer): a blank, every value matched or left
  // out, the preview loaded, and at least one Ready variant.
  const built = garmentChosen && complete && !previewLoading && !previewError && ok?.anatomy != null;
  const canSave = built && (ok?.counts.ready ?? 0) > 0;
  // The first unmet gate is named once: by the locked step's own amber line,
  // or by the footer when every step is open and nothing can be built.
  const gateHelper = built && !canSave ? t('supply.gate.nothingReady') : null;
  const describedBy = !garmentChosen ? step2HelperId : !complete ? step3HelperId : undefined;

  return (
    <div className="flex flex-col gap-4" data-testid="supply-composer">
      {/* Always mounted, so a step unlocking is announced (DESIGN.md §16). */}
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
      <ComposerStep label={null} locked={false} testId="supply-step-1">
        <SharedSkuPicker
          mode="garment"
          id={`supply-garment-${product.id}`}
          label={t('supply.step1')}
          ownerOrganizationId={owner.id}
          ownerName={owner.name}
          value={draft.garment}
          disabled={garmentLocked}
          onGarmentChange={(garment) => {
            focusStep2.current = true;
            setAnnouncement(t('supply.step2'));
            composer.setGarment(garment);
          }}
          useGarmentOptions={useGarmentOptions}
        />
        {garmentLocked && (
          <p className="text-xs leading-normal text-muted-foreground" data-testid="supply-garment-locked">
            {t('supply.lock.garment')}
          </p>
        )}
        {garmentChosen && (split !== null || splittable.colour || splittable.size) && (
          <SplitControl
            productId={product.id}
            split={split}
            splittable={splittable}
            locked={splitLocked}
            onSplitChange={composer.setSplit}
          />
        )}
      </ComposerStep>
      <ComposerStep
        label={t('supply.step2')}
        locked={!garmentChosen}
        lockedHelper={t('supply.picker.placeholder')}
        helperId={step2HelperId}
        stepRef={step2Ref}
        testId="supply-step-2"
      >
        {step2Body}
      </ComposerStep>
      <ComposerStep
        label={t('supply.step3')}
        locked={!complete}
        lockedHelper={t('supply.gate.match')}
        helperId={step3HelperId}
        stepRef={step3Ref}
        testId="supply-step-3"
      >
        {complete && ok && (
          <SupplyPreviewStep
            preview={ok}
            ownerName={owner.name}
            loading={previewLoading}
            error={previewError}
            onRetry={composer.retryPreview}
            idPrefix={`supply-preview-${product.id}`}
          />
        )}
      </ComposerStep>
      <SupplyFooter
        composer={composer}
        canSave={canSave}
        gateHelper={gateHelper}
        describedBy={describedBy}
        onDiscard={() => {
          composer.discard();
          onDiscard?.();
        }}
      />
    </div>
  );
}

/**
 * The quiet link that opens "Per colour / Per size", and the switch
 * with its way back to one base garment. An option the product has one value
 * of cannot split it, so its segment is not offered. Shared with the own base
 * garment editor (OwnBlankGroupsPanel), which passes its own productId prefix
 * so the two never share an id.
 */
export function SplitControl({
  productId,
  split,
  splittable,
  locked = false,
  onSplitChange,
}: {
  productId: string;
  split: SupplyMatchGroup | null;
  splittable: { colour: boolean; size: boolean };
  /** BLU-1522 S2b: a synced variant sits on another base garment, so the split cannot change or close. */
  locked?: boolean;
  onSplitChange: (split: SupplyMatchGroup | null) => void;
}) {
  const { t } = useTranslation('productSync');
  // The link and the switch swap places: keyboard focus follows to the other one.
  const linkRef = useRef<HTMLButtonElement>(null);
  const switchRef = useRef<HTMLDivElement>(null);
  const focusNext = useRef<'link' | 'switch' | null>(null);
  useEffect(() => {
    const target = focusNext.current;
    focusNext.current = null;
    if (target === 'link') linkRef.current?.focus();
    if (target === 'switch') switchRef.current?.querySelector<HTMLElement>('button[aria-pressed="true"]')?.focus();
  }, [split]);
  if (!split) {
    return (
      <Button
        ref={linkRef}
        type="button"
        variant="link"
        className="h-auto w-fit p-0 text-xs font-normal text-muted-foreground underline underline-offset-2 hover:text-foreground motion-reduce:transition-none"
        onClick={() => {
          focusNext.current = 'switch';
          onSplitChange(splittable.colour ? 'colour' : 'size');
        }}
      >
        {t('supply.groups.open')}
      </Button>
    );
  }
  // The open option stays listed even when the last preview could not split by it.
  const options: Array<{ value: SupplyMatchGroup; label: string }> = [];
  if (splittable.colour || split === 'colour') options.push({ value: 'colour', label: t('supply.groups.perColour') });
  if (splittable.size || split === 'size') options.push({ value: 'size', label: t('supply.groups.perSize') });
  return (
    <div ref={switchRef} id={`supply-split-${productId}`} className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <SegmentedControl<SupplyMatchGroup>
          size="sm"
          ariaLabel={t('supply.groups.splitLabel')}
          value={split}
          onChange={onSplitChange}
          className="max-w-full"
          options={options}
          disabled={locked}
        />
        <Button
          type="button"
          variant="link"
          disabled={locked}
          aria-describedby={locked ? `supply-split-locked-${productId}` : undefined}
          className="h-auto p-0 text-xs font-normal text-muted-foreground underline underline-offset-2 hover:text-foreground motion-reduce:transition-none"
          onClick={() => {
            focusNext.current = 'link';
            onSplitChange(null);
          }}
        >
          {t('supply.groups.close')}
        </Button>
      </div>
      {locked && (
        <p
          id={`supply-split-locked-${productId}`}
          className="text-xs leading-normal text-muted-foreground"
          data-testid="supply-split-locked"
        >
          {t('supply.lock.split')}
        </p>
      )}
    </div>
  );
}

/**
 * The footer (UI-SPEC 1.2): "Discard draft" then "Use this blank", right
 * aligned. While saving, the primary shows its spinner and ignores clicks but
 * is never `disabled`, so focus stays on it (UI 31); "Discard draft" stays
 * available. Saved: the row becomes the saved note (no toast, §15.4), and
 * focus moves to the note if the button had it. A refusal shows as one line
 * under the row (its sticky toast comes from the hook).
 */
function SupplyFooter({
  composer,
  canSave,
  gateHelper,
  describedBy,
  onDiscard,
}: {
  composer: ReturnType<typeof useSupplyComposer>;
  canSave: boolean;
  /** The footer's own amber gate line (only when no step's line already names it). */
  gateHelper: string | null;
  /** Id of the step line that says why the primary is disabled. */
  describedBy: string | undefined;
  onDiscard: () => void;
}) {
  const { t } = useTranslation('productSync');
  const { saved, saving, refusalMessage } = composer;
  const savedRef = useRef<HTMLDivElement>(null);
  const gateId = useId();
  const refusalId = useId();

  useEffect(() => {
    // The focused button is gone; hand focus to the note that replaced it.
    if (saved && (document.activeElement === document.body || document.activeElement === null)) {
      savedRef.current?.focus();
    }
  }, [saved]);

  if (saved) {
    return (
      <div ref={savedRef} tabIndex={-1} className="border-t pt-4 focus-visible:outline-none" data-testid="supply-saved">
        <NoteLine icon={CheckCircle2} className="leading-normal">
          {t('supply.savedNote')}
        </NoteLine>
      </div>
    );
  }

  const describedByIds =
    [describedBy, gateHelper ? gateId : null, refusalMessage ? refusalId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className="flex flex-col gap-2 border-t pt-4" data-testid="supply-footer">
      <div className="flex flex-wrap items-center justify-end gap-2">
        {gateHelper && (
          <p id={gateId} className="mr-auto text-xs leading-normal text-amber-700">
            {gateHelper}
          </p>
        )}
        <Button
          type="button"
          variant="ghost"
          className="h-8 gap-1.5 px-3 text-xs motion-reduce:transition-none"
          onClick={onDiscard}
        >
          {t('supply.footer.discard')}
        </Button>
        <Button
          type="button"
          className="h-8 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90 motion-reduce:transition-none"
          // Never disabled while saving (UI 31): focus stays, re-clicks are ignored.
          disabled={!canSave && !saving}
          aria-busy={saving || undefined}
          aria-describedby={describedByIds}
          onClick={() => {
            if (saving) return;
            void composer.save();
          }}
        >
          {saving && <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
          {t('supply.footer.use')}
        </Button>
      </div>
      {refusalMessage && (
        <p
          id={refusalId}
          className="flex items-start gap-1 text-xs leading-normal text-destructive"
          data-testid="supply-refusal"
        >
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
          <span>{refusalMessage}</span>
        </p>
      )}
    </div>
  );
}
